/**
 * Screen-docked tour card. Portals to document.body; the anchor decides
 * where it docks (splash centered, compact tucked into a corner), CSS custom
 * properties give each variant its type ramp and spacing so a step lays out
 * cleanly in both.
 */

import { PROMPT_DIALOG_STYLES } from "@/template-prompt/promptDialogStyles";
import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { AnchorId } from "../types";

/** Exit keyframe length; the runner waits this long before moving on. */
export const STEP_EXIT_MS = 320;

export type AnchorVariant = "splash" | "compact";

export interface SafeInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const DEFAULT_SAFE_INSETS: SafeInsets = {
  top: 16,
  right: 16,
  bottom: 16,
  left: 16,
};

interface Viewport {
  width: number;
  height: number;
}

export interface AnchorRect {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  height: number | "auto";
}

const COMPACT_W = 520;
const COMPACT_MARGIN = 24;
const NARROW_W = Math.round(COMPACT_W * 0.6);
const NARROW_MARGIN = COMPACT_MARGIN * 2 + 16;
const SPLASH_W_FRACTION = 0.6;
const SPLASH_MIN_W = 640;
const SPLASH_MAX_W = 1280;
const TOP_SPLASH_OFFSET = 24;
const WIDE_LEFT_PAD = 56;
const WIDE_TOP_EXTRA = 8;
const LEFT_TOOLBAR_CLEARANCE = 80;
const LEFT_CENTER_GAP = 16;
const RIGHT_COMPACT_GAP = 16;
const TOP_CENTER_COMPACT_OFFSET = 24;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function splashWidth(viewport: Viewport, insets: SafeInsets) {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  const targetW = clamp(
    Math.round(viewport.width * SPLASH_W_FRACTION),
    SPLASH_MIN_W,
    SPLASH_MAX_W,
  );
  const width = Math.min(targetW, safeW);
  const left = insets.left + Math.round((safeW - width) / 2);
  return { width, left };
}

function compactRect(
  corner: "tl" | "tr" | "bl" | "br",
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(
    0,
    viewport.width - insets.left - insets.right - COMPACT_MARGIN * 2,
  );
  const width = Math.min(COMPACT_W, safeW);
  const left =
    corner === "tl" || corner === "bl"
      ? insets.left + COMPACT_MARGIN
      : viewport.width - insets.right - COMPACT_MARGIN - width;
  if (corner === "tl" || corner === "tr") {
    return {
      top: Math.max(insets.top, insets.top + COMPACT_MARGIN),
      left: Math.max(insets.left, left),
      width,
      height: "auto",
    };
  }
  return {
    bottom: Math.max(insets.bottom, insets.bottom + COMPACT_MARGIN),
    left: Math.max(insets.left, left),
    width,
    height: "auto",
  };
}

function centeredTop(
  viewport: Viewport,
  insets: SafeInsets,
  approxH: number,
): number {
  const safeH = Math.max(0, viewport.height - insets.top - insets.bottom);
  return insets.top + Math.max(0, Math.round((safeH - approxH) / 2));
}

export function resolveAnchor(
  id: AnchorId,
  viewport: Viewport,
  insets: SafeInsets = DEFAULT_SAFE_INSETS,
): AnchorRect {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  switch (id) {
    case "centerSplash": {
      // Content-driven height; the card measures itself and centers (see
      // `topOverride`). `top` is a first-paint fallback.
      const { width, left } = splashWidth(viewport, insets);
      return { top: insets.top, left, width, height: "auto" };
    }
    case "topSplash": {
      const { width, left } = splashWidth(viewport, insets);
      return {
        top: insets.top + TOP_SPLASH_OFFSET,
        left,
        width,
        height: "auto",
      };
    }
    case "centerCompact": {
      const width = Math.min(COMPACT_W, safeW);
      const left = insets.left + Math.round((safeW - width) / 2);
      return {
        top: centeredTop(viewport, insets, 260),
        left,
        width,
        height: "auto",
      };
    }
    case "topCenterCompact": {
      const width = Math.min(COMPACT_W, safeW);
      const left = insets.left + Math.round((safeW - width) / 2);
      return {
        top: insets.top + TOP_CENTER_COMPACT_OFFSET,
        left,
        width,
        height: "auto",
      };
    }
    case "topLeftCompact":
      return compactRect("tl", viewport, insets);
    case "topLeftCompactWide": {
      const wideSafeW = Math.max(0, safeW - WIDE_LEFT_PAD - COMPACT_MARGIN);
      return {
        top: insets.top + COMPACT_MARGIN + WIDE_TOP_EXTRA,
        left: Math.max(insets.left, insets.left + WIDE_LEFT_PAD),
        width: Math.min(COMPACT_W, wideSafeW),
        height: "auto",
      };
    }
    case "topRightCompact":
      return compactRect("tr", viewport, insets);
    case "bottomLeftCompact":
      return compactRect("bl", viewport, insets);
    case "bottomLeftNarrow": {
      const narrowSafeW = Math.max(0, safeW - NARROW_MARGIN * 2);
      return {
        bottom: Math.max(insets.bottom, insets.bottom + NARROW_MARGIN),
        left: Math.max(insets.left, insets.left + NARROW_MARGIN),
        width: Math.min(NARROW_W, narrowSafeW),
        height: "auto",
      };
    }
    case "leftCenterNarrow": {
      const narrowSafeW = Math.max(
        0,
        safeW - LEFT_TOOLBAR_CLEARANCE - LEFT_CENTER_GAP,
      );
      const left = insets.left + LEFT_TOOLBAR_CLEARANCE + LEFT_CENTER_GAP;
      return {
        top: centeredTop(viewport, insets, 220),
        left: Math.max(insets.left, left),
        width: Math.min(NARROW_W, narrowSafeW),
        height: "auto",
      };
    }
    case "bottomRightCompact":
      return compactRect("br", viewport, insets);
    case "rightCenterCompact": {
      const rightSafeW = Math.max(
        0,
        safeW - RIGHT_COMPACT_GAP - COMPACT_MARGIN,
      );
      const width = Math.min(NARROW_W, rightSafeW);
      const left = viewport.width - insets.right - COMPACT_MARGIN - width;
      return {
        top: insets.top,
        left: Math.max(insets.left, left),
        width,
        height: "auto",
      };
    }
  }
}

export function variantForAnchor(id: AnchorId): AnchorVariant {
  return id === "centerSplash" || id === "topSplash" ? "splash" : "compact";
}

function readViewport(): Viewport {
  if (typeof window === "undefined") return { width: 1280, height: 800 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function useViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(() => readViewport());
  useEffect(() => {
    const onResize = () => setVp(readViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vp;
}

interface TourCardProps {
  anchor: AnchorId;
  /** Remounts the step subtree so entrance keyframes replay. */
  contentKey: string;
  /** Plays the exit keyframes. */
  outgoing: boolean;
  safeInsets?: SafeInsets;
  children: ReactNode;
}

export const TourCard = memo(function TourCard({
  anchor,
  contentKey,
  outgoing,
  safeInsets = DEFAULT_SAFE_INSETS,
  children,
}: TourCardProps) {
  const viewport = useViewport();
  const reducedMotion = usePrefersReducedMotion();

  // Measured height so self-centering anchors center accurately and float
  // to the top of the safe area when taller than the viewport.
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => setCardHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentKey]);

  const variant = variantForAnchor(anchor);
  const rect = resolveAnchor(anchor, viewport, safeInsets);

  let topOverride: number | undefined;
  const measuredCenterAnchor =
    anchor === "rightCenterCompact" || anchor === "centerSplash";
  if (measuredCenterAnchor && cardHeight != null) {
    const safeH = Math.max(
      0,
      viewport.height - safeInsets.top - safeInsets.bottom,
    );
    const minVMargin = anchor === "centerSplash" ? 32 : 0;
    topOverride =
      safeInsets.top +
      Math.max(minVMargin, Math.round((safeH - cardHeight) / 2));
  }

  return createPortal(
    <>
      <style>{PROMPT_DIALOG_STYLES}</style>
      <style>{CARD_STYLES}</style>
      <div
        ref={cardRef}
        className={`_co_onb _co_onb_overlay _co_onb_overlay_${variant}${
          anchor === "centerCompact" ? " _co_onb_overlay_center_compact" : ""
        }${
          anchor === "rightCenterCompact" ? " _co_onb_overlay_right_center" : ""
        }${reducedMotion ? " _co_onb_overlay_no_motion" : ""}`}
        style={{
          position: "fixed",
          ...(rect.bottom !== undefined
            ? { bottom: rect.bottom }
            : { top: topOverride ?? rect.top }),
          left: rect.left,
          width: rect.width,
          ...(rect.height !== "auto" ? { height: rect.height } : {}),
          zIndex: 55,
          color: "var(--co-fg)",
          fontFamily:
            "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif",
          overflow: rect.height === "auto" ? "visible" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        role="dialog"
        aria-label="Cohesive onboarding"
      >
        <div
          key={contentKey}
          className={
            outgoing
              ? "_co_onb_step_exit _co_onb_overlay_content"
              : "_co_onb_overlay_content"
          }
        >
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
});

// Per-variant tokens (`--co-onb-fs-*`, `--co-onb-stack-*`, `--co-onb-pad-*`)
// consumed by the step components; tuned against compact ~520 wide and
// splash ~860 wide on a 1440 viewport.
const CARD_STYLES = `
  ._co_onb_overlay {
    transition:
      top 320ms cubic-bezier(0.2, 0, 0, 1),
      bottom 320ms cubic-bezier(0.2, 0, 0, 1),
      left 320ms cubic-bezier(0.2, 0, 0, 1),
      width 320ms cubic-bezier(0.2, 0, 0, 1);
  }
  ._co_onb_overlay_splash {
    --co-onb-fs-eyebrow: 12px;
    --co-onb-fs-heading: 38px;
    --co-onb-fs-heading-display: 56px;
    --co-onb-fs-lede: 17px;
    --co-onb-fs-hint: 12px;
    --co-onb-fs-meta: 16px;
    --co-onb-fs-cta: 14px;
    --co-onb-stack-xs: 6px;
    --co-onb-stack-sm: 12px;
    --co-onb-stack-md: 20px;
    --co-onb-stack-lg: 32px;
    --co-onb-stack-xl: 44px;
    --co-onb-pad-x: 40px;
    --co-onb-pad-y: 36px;
    --co-onb-content-max: 560px;
    --co-onb-dot: 9px;

    background: rgba(243, 239, 230, 0.97);
    backdrop-filter: blur(18px) saturate(1.08);
    -webkit-backdrop-filter: blur(18px) saturate(1.08);
    border: 1px solid var(--co-border);
    border-radius: 22px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 28px 80px rgba(15, 12, 8, 0.28);
  }
  .dark ._co_onb_overlay_splash {
    background: rgba(26, 26, 22, 0.97);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 32px 96px rgba(0, 0, 0, 0.6);
  }
  ._co_onb_overlay_compact {
    --co-onb-fs-eyebrow: 11px;
    --co-onb-fs-heading: 28px;
    --co-onb-fs-heading-display: 38px;
    --co-onb-fs-lede: 15px;
    --co-onb-fs-hint: 11px;
    --co-onb-fs-meta: 14px;
    --co-onb-fs-cta: 13px;
    --co-onb-stack-xs: 4px;
    --co-onb-stack-sm: 8px;
    --co-onb-stack-md: 14px;
    --co-onb-stack-lg: 22px;
    --co-onb-stack-xl: 30px;
    --co-onb-pad-x: 28px;
    --co-onb-pad-y: 26px;
    --co-onb-content-max: 100%;
    --co-onb-dot: 7px;

    background: var(--co-paper);
    border: 1px solid var(--co-border);
    border-radius: 18px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 12px 36px rgba(15, 12, 8, 0.18);
  }
  .dark ._co_onb_overlay_compact {
    background: rgba(26, 26, 22, 0.96);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 16px 48px rgba(0, 0, 0, 0.55);
  }
  ._co_onb_overlay_content {
    flex: 1 1 auto;
    width: 100%;
    box-sizing: border-box;
    padding: var(--co-onb-pad-y) var(--co-onb-pad-x);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    text-align: left;
    gap: var(--co-onb-stack-md);
    overflow: hidden;
  }
  ._co_onb_overlay_center_compact {
    --co-onb-fs-eyebrow: 11px;
    --co-onb-fs-heading: 28px;
    --co-onb-fs-heading-display: 38px;
    --co-onb-fs-lede: 15px;
    --co-onb-fs-hint: 11px;
    --co-onb-fs-meta: 14px;
    --co-onb-fs-cta: 13px;
    --co-onb-pad-x: 28px;
    --co-onb-pad-y: 26px;
  }
  ._co_onb_overlay_right_center { --co-onb-pad-y: var(--co-onb-pad-x); }
  ._co_onb_overlay_no_motion { transition: none !important; }
  @media (prefers-reduced-motion: reduce) {
    ._co_onb_overlay { transition: none; }
  }
`;
